import React from "react";

import { Box, BoxSx, Target } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";
import { Button } from "../Button";
import { FinalPaginationProps } from "./Pagination";

type NumberButtonProps = React.ComponentProps<typeof Target> & {
  active?: boolean;
  page: number;
  onPageChange: (index: number) => void;
  disabled?: boolean;
};

const NumberButton: React.FC<NumberButtonProps> = ({
  active = false,
  onPageChange,
  page,
  disabled = false
}) => {
  const colorClassName = `pagination${useId().replace(/^[A-Z0-9]gi/, "")}`;
  const handleClick = React.useCallback(() => {
    onPageChange(page);
  }, [onPageChange, page]);

  // mimicked transparent icon button styles
  const buttonStyles: BoxSx = {
    // layout
    display: "flex",
    justifyContent: "center",
    alignItems: "end",

    // appearance
    color: disabled
      ? "tab-text-disabled"
      : active
        ? "tab-stroke-selected"
        : "unset",
    transition: "transition-color-medium",
    px: "spacing-3",
    [`.${colorClassName}`]: {
      borderBottomColor:
        disabled && active
          ? "tab-text-disabled"
          : active
            ? "tab-stroke-selected"
            : "transparent",
      transition: "transition-color-medium"
    },
    ...(!active && {
      [`:hover .${colorClassName}`]: {
        borderBottomColor: disabled ? "transparent" : "tab-stroke-hover"
      },
      [`:active .${colorClassName}`]: {
        borderBottomColor: disabled ? "transparent" : "tab-stroke-click"
      }
    }),
    ":focus-visible": {
      outlineStyle: "solid",
      borderRadius: "radius-1",
      outlineColor: "focus",
      outlineWidth: "2px"
    }
  };

  return (
    <Target
      aria-selected={active}
      aria-label={`Go to page ${page}`}
      role="tab"
      disabled={disabled}
      sx={buttonStyles}
      onClick={handleClick}
    >
      <Text
        className={colorClassName}
        variant="title-1-medium"
        sx={{
          px: "spacing-2",
          pb: "spacing-4",
          borderBottomWidth: "3px",
          borderBottomStyle: "solid"
        }}
      >
        {page}
      </Text>
    </Target>
  );
};

const getNextPrevButtonDisplay = (hidden: boolean) =>
  hidden ? "none" : "flex";

interface ExtendedPaginationProps extends FinalPaginationProps {
  siblingCount?: number;
}

const NumberedPagination: React.FC<ExtendedPaginationProps> = ({
  count,
  handleClickNext,
  handleClickPrev,
  handleKeyUp,
  handlePageChange,
  hasNext,
  hasPrev,
  hideNextPrevButtonsArray,
  page,
  siblingCount = 1,
  disabled
}) => {
  // https://github.com/mui/material-ui/blob/master/packages/mui-material/src/usePagination/usePagination.js
  const getPageNumbers = () => {
    const boundaryCount = 1; // the number of always visible pages at the beginning and end
    const range = (start: number, end: number) => {
      const length = end - start + 1;
      return Array.from({ length }, (_, i) => start + i);
    };

    const startPages = range(1, Math.min(boundaryCount, count));
    const endPages = range(
      Math.max(count - boundaryCount + 1, boundaryCount + 1),
      count
    );

    const siblingsStart = Math.max(
      Math.min(
        // Natural start
        page - siblingCount,
        // Lower boundary when page is high
        count - boundaryCount - siblingCount * 2 - 1
      ),
      // Greater than startPages
      boundaryCount + 2
    );

    const siblingsEnd = Math.min(
      Math.max(
        // Natural end
        page + siblingCount,
        // Upper boundary when page is low
        boundaryCount + siblingCount * 2 + 2
      ),
      // Less than endPages
      count - boundaryCount - 1
    );

    const pages = [
      ...startPages,

      // Start ellipsis
      ...(siblingsStart > boundaryCount + 2
        ? (["..."] as "..."[])
        : boundaryCount + 1 < count - boundaryCount
          ? [boundaryCount + 1]
          : []),

      // Sibling pages
      ...Array.from(
        { length: siblingsEnd - siblingsStart + 1 },
        (_, i) => siblingsStart + i
      ),

      // End ellipsis
      ...(siblingsEnd < count - boundaryCount - 1
        ? (["..."] as "..."[])
        : count - boundaryCount > boundaryCount
          ? [count - boundaryCount]
          : []),

      ...endPages
    ];

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center"
      }}
    >
      <Box
        onKeyUp={handleKeyUp}
        role="tablist"
        sx={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          listStyle: "none",
          minHeight: "spacing-10"
        }}
      >
        <Button
          icon="caret-left"
          aria-label="Go to previous page"
          variant="subtle"
          onClick={handleClickPrev}
          role="tab"
          tabIndex={hasPrev ? 0 : -1}
          disabled={disabled || !hasPrev}
          sx={{
            display: [
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[0]),
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[1]),
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[2])
            ],
            color:
              disabled || !hasPrev ? "tab-text-disabled" : "tab-text-default",
            mt: -2,
            alignSelf: "center"
          }}
        />
        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === "...") {
            return (
              <Box
                key={`ellipsis-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "spacing-2",
                  mx: "spacing-3",
                  whiteSpace: "nowrap",
                  color: "tab-text-disabled"
                }}
              >
                ...
              </Box>
            );
          }
          return (
            <NumberButton
              key={`page-${pageNumber}-${page}`}
              active={page === pageNumber}
              page={pageNumber}
              onPageChange={handlePageChange}
              disabled={disabled}
            />
          );
        })}
        <Button
          icon="caret-right"
          variant="subtle"
          aria-label="Go to next page"
          onClick={handleClickNext}
          role="tab"
          tabIndex={hasNext ? 0 : -1}
          disabled={disabled || !hasNext}
          sx={{
            display: [
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[0]),
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[1]),
              getNextPrevButtonDisplay(hideNextPrevButtonsArray[2])
            ],
            color:
              disabled || !hasNext ? "tab-text-disabled" : "tab-text-default",
            mt: -2,
            alignSelf: "center"
          }}
        />
      </Box>
    </Box>
  );
};

export default NumberedPagination;
